import { useState, useRef, useEffect, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import type { PanelMenuItem } from "@/data/panels";
import { useAuth } from "@/hooks/useAuth";
import { resolvePanelUser } from "@/lib/profile";
import RoleSwitcher from "./RoleSwitcher";
import NotificationBell from "./NotificationBell";
import {useAiCoach} from "@/hooks/useAiCoach";
import { assistantAvatar } from "@/mocks/aiCoach";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { getDataClient } from "@/lib/dataClient";

interface RoleTopbarProps {
  menu: PanelMenuItem[];
  homePath: string;
  homeLabel: string;
  roleLabel: string;
  privateRole: boolean;
}

export default function RoleTopbar({
  menu,
  homePath,
  homeLabel,
  roleLabel,
  privateRole,
}: RoleTopbarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openCoach } = useAiCoach();
  const { user } = useAuth();
  const identity = resolvePanelUser(user);
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const identityRef = useRef<HTMLDivElement>(null);

  // Role göre profil düzenleme hedefi (yöneticide kişisel profil yok, ayarlara yönlendirir).
  const profileInfo = (() => {
    switch (homePath) {
      case "/ogrenci":
        return { href: "/ogrenci/profilim", label: "Profil Düzenle", icon: "ri-user-settings-line" };
      case "/ogretmen":
        return { href: "/ogretmen/profil", label: "Profil Düzenle", icon: "ri-user-settings-line" };
      case "/veli":
        return { href: "/veli/profil", label: "Profil Düzenle", icon: "ri-user-settings-line" };
      default:
        return { href: "/yonetici/ayarlar", label: "Sistem Ayarları", icon: "ri-settings-3-line" };
    }
  })();

  const current = menu.find((m) => pathname === m.href && m.href !== homePath);

  const results = query.trim()
    ? menu.filter((m) => m.label.toLocaleLowerCase("tr-TR").includes(query.trim().toLocaleLowerCase("tr-TR")))
    : [];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!identityOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (identityRef.current && !identityRef.current.contains(e.target as Node)) {
        setIdentityOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [identityOpen]);

  const handleLogout = async () => {
    setIdentityOpen(false);
    const dataClient = getDataClient();
    await dataClient.auth.signOut();
    navigate("/", { replace: true });
  };

  const select = (href: string) => {
    setOpen(false);
    setQuery("");
    setActiveIdx(-1);
    navigate(href);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && results[activeIdx]) select(results[activeIdx].href);
    }
  };

  return (
    <header className="sticky top-0 z-20 bg-background-50 border-b border-background-200">
      <div className="flex items-center gap-3 h-16 px-4 md:px-6">
        {/* Breadcrumb / sayfa başlığı */}
        <nav className="hidden md:flex items-center gap-1.5 text-sm whitespace-nowrap" aria-label="Konum">
          <Link to={homePath} className="text-foreground-500 hover:text-foreground-800 transition-colors">
            {homeLabel}
          </Link>
          {current && (
            <>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
              <span className="text-foreground-900 font-medium" aria-current="page">
                {current.label}
              </span>
            </>
          )}
        </nav>
        <span className="md:hidden flex-1 min-w-0 text-sm font-semibold text-foreground-900 truncate">
          {current?.label ?? homeLabel}
        </span>

        <div className="ml-auto flex items-center gap-1.5 md:gap-2 min-w-0">
          {/* Panel içi arama */}
          <div className="relative w-40 sm:w-56 lg:w-64 shrink-0" ref={wrapRef}>
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" aria-hidden="true"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setActiveIdx(-1);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder="Sayfa ara..."
              role="combobox"
              aria-expanded={open && results.length > 0}
              aria-controls="panel-search-results"
              aria-autocomplete="list"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />

            {open && query.trim() && (
              <div
                id="panel-search-results"
                role="listbox"
                className="absolute left-0 right-0 mt-2 rounded-lg border border-background-200 bg-background-50 p-1.5 z-50 animate-fade-down"
              >
                {results.length === 0 ? (
                  <p className="px-3 py-2.5 text-sm text-foreground-500">Eşleşen sayfa bulunamadı</p>
                ) : (
                  results.map((m, i) => (
                    <button
                      key={m.href}
                      type="button"
                      role="option"
                      aria-selected={i === activeIdx}
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => select(m.href)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm text-left cursor-pointer transition-colors ${
                        i === activeIdx ? "bg-background-100 text-foreground-900" : "text-foreground-700"
                      }`}
                    >
                      <i className={`${m.icon} text-base text-foreground-400`} aria-hidden="true"></i>
                      {m.label}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <NotificationBell />

          <button
            type="button"
            onClick={() => openCoach()}
            className="hidden md:flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors"
          >
            <img src={assistantAvatar} alt={`${name} Asistan`} className="w-6 h-6 rounded-full object-cover" />
            <span className="hidden lg:inline">{name} Asistan</span>
          </button>

          <RoleSwitcher locked={privateRole} roleLabel={roleLabel} />

          {/* Kullanıcı kimliği */}
          <div className="relative" ref={identityRef}>
            <button
              type="button"
              onClick={() => setIdentityOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={identityOpen}
              className="hidden sm:flex items-center gap-2.5 pl-2 ml-1 border-l border-background-200 cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-950 text-background-50 font-heading font-semibold text-sm">
                {identity.initials || "K"}
              </span>
              <div className="leading-tight hidden lg:block text-left">
                <p className="text-sm font-semibold text-foreground-950 truncate">
                  {identity.name || "Kullanıcı"}
                </p>
                <p className="text-xs text-foreground-500 truncate">{identity.email}</p>
              </div>
              <i
                className={`hidden lg:inline ri-arrow-down-s-line text-base text-foreground-400 transition-transform ${
                  identityOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {identityOpen && (
              <div
                role="menu"
                aria-label="Hesap menüsü"
                className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-background-200 bg-background-50 p-1.5 z-50 animate-fade-down"
              >
                <div className="px-3 py-2 mb-1 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-900 truncate">
                    {identity.name || "Kullanıcı"}
                  </p>
                  <p className="text-xs text-foreground-500 truncate">{identity.email}</p>
                </div>
                <Link
                  to={profileInfo.href}
                  role="menuitem"
                  onClick={() => setIdentityOpen(false)}
                  className="flex items-center justify-between gap-3 w-full rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <span className="whitespace-nowrap">{profileInfo.label}</span>
                  <i className={`${profileInfo.icon} text-foreground-400`} aria-hidden="true"></i>
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex items-center justify-between gap-3 w-full rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <span className="whitespace-nowrap">Çıkış Yap</span>
                  <i className="ri-logout-box-r-line text-foreground-400" aria-hidden="true"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
